// src/components/layouts/admindashboard/header/sub/NotificationButton.tsx

import React, { useState, useRef, useEffect } from "react";
import { Bell, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const notifications = [
  {
    id: 1,
    title: "Nouveau message",
    description: "Vous avez reçu un message de Thomas",
    time: "Il y a 5 min",
    read: false,
    type: "message",
  },
  {
    id: 2,
    title: "Projet validé",
    description: "Le projet 'Branding Braincrea' a été approuvé",
    time: "Il y a 2 heures",
    read: false,
    type: "success",
  },
  {
    id: 3,
    title: "Rappel",
    description: "Réunion avec l'équipe à 15h",
    time: "Hier",
    read: true,
    type: "reminder",
  },
];

const NotificationButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState(notifications);
  const menuRef = useRef<HTMLDivElement>(null);
  const unreadCount = items.filter((n) => !n.read).length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = (id: number) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, read: true } : item))
    );
  };

  const markAllAsRead = () => {
    setItems((prev) => prev.map((item) => ({ ...item, read: true })));
  };

  return (
    <div className="sm:relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-black/20 text-white border border-white/30 backdrop-blur-sm hover:bg-accent hover:text-black hover:scale-105 transition-all duration-200 shadow-md"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-accent text-black rounded-full ring-2 ring-white/30 animate-pulse">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-5 sm:right-0 mt-2 w-80 rounded-2xl bg-black/95 backdrop-blur-md border border-accent/30 shadow-2xl overflow-hidden z-50"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-accent/20 bg-gradient-to-r from-secondary/20 to-transparent">
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-accent hover:text-white transition-colors"
                >
                  Tout marquer comme lu
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  Aucune notification
                </div>
              ) : (
                items.map((notif) => (
                  <div
                    key={notif.id}
                    className={`relative px-4 py-3 border-b border-white/10 transition-all duration-150 hover:bg-white/5 cursor-pointer ${
                      !notif.read ? "bg-accent/5" : ""
                    }`}
                    onClick={() => markAsRead(notif.id)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {notif.type === "success" ? (
                          <CheckCircle size={16} className="text-accent" />
                        ) : (
                          <Bell size={16} className="text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{notif.title}</p>
                        <p className="text-xs text-gray-300 mt-1">{notif.description}</p>
                        <p className="text-[10px] text-gray-500 mt-1">{notif.time}</p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-accent rounded-full mt-2 shadow-[0_0_4px_#FBC02D]" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="px-4 py-2 border-t border-accent/20 text-center">
              <a
                href="#"
                className="text-xs text-accent hover:text-white transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Voir toutes les notifications
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationButton;